import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { LanguageService } from '../../../shared/services/language.service';
import { TranslationService } from '../../../shared/services/translation.service';

@Component({
  selector: 'app-product-actions',
  standalone: false,
  templateUrl: './product-actions.component.html',
  styleUrl: './product-actions.component.scss',
})
export class ProductActionsComponent implements OnInit {
  constructor(
    private readonly languageService: LanguageService,
    private readonly translationService: TranslationService,
  ) {}

  @Input() quantity!: number;
  @Input() saleUnit!: number;
  @Input() product: any;

  @Output() quantityIncrement = new EventEmitter<void>();
  @Output() quantityDecrement = new EventEmitter<void>();
  @Output() addToCartClicked = new EventEmitter<any>();

  ngOnInit(): void {}

  get canOrderNormally(): boolean {
    return !!this.product &&
      this.product.status !== 'DISABLED' &&
      this.product.status !== 'ARCHIVED';
  }

  get canAddToCart(): boolean {
    return this.canOrderNormally;
  }

  get hasBackorderAvailability(): boolean {
    const stockQuantity = Number(this.product?.quantity);
    return Number(this.product?.availability) === 1 ||
      (Number.isFinite(stockQuantity) && stockQuantity <= 0);
  }

  get unitLabel(): string {
    const unit = this.product?.unit_sales || this.product?.unit_product;
    const language = this.languageService.currentLanguage;
    const translatedLabel = unit?.[`label_${language}`];
    if (typeof translatedLabel === 'string' && translatedLabel.trim()) {
      return translatedLabel;
    }

    const sourceLabel = String(unit?.label || '').trim();
    return sourceLabel
      ? this.translationService.translateLiteralText(sourceLabel, 'pl')
      : language === 'en'
        ? 'pcs.'
        : language === 'fr'
          ? 'pièce'
          : 'szt.';
  }

  get quantityUnitLabel(): string {
    return this.quantity === (this.saleUnit || 1)
      ? this.getSingularForm(this.unitLabel)
      : this.unitLabel;
  }

  decrementQuantity(): void {
    this.quantityDecrement.emit();
  }

  incrementQuantity(): void {
    this.quantityIncrement.emit();
  }

  addToCart(): void {
    this.addToCartClicked.emit();
  }

  getSingularForm(label: string): string {
    if (!label) return 'szt.';
    
    // Normalize to lowercase for comparison
    const lowerLabel = label.toLowerCase();
    
    // Handle common plural endings
    if (lowerLabel.endsWith('pieces')) return 'piece';
    if (lowerLabel.endsWith('items')) return 'item';
    if (lowerLabel.endsWith('units')) return 'unit';
    if (lowerLabel.endsWith('boxes')) return 'box';
    if (lowerLabel.endsWith('packs')) return 'pack';
    if (lowerLabel.endsWith('packets')) return 'packet';
    if (lowerLabel.endsWith('bottles')) return 'bottle';
    if (lowerLabel.endsWith('bags')) return 'bag';
    if (lowerLabel.endsWith('cases')) return 'case';
    if (lowerLabel.endsWith('sets')) return 'set';
    if (lowerLabel.endsWith('rolls')) return 'roll';
    
    // Special handling for measurement tools
    if (lowerLabel.includes('piece')) return 'piece';
    if (lowerLabel.includes('tool')) return 'tool';
    if (lowerLabel.includes('instrument')) return 'instrument';
    
    // If it ends with 's' but not 'ss', remove the 's'
    if (lowerLabel.endsWith('s') && !lowerLabel.endsWith('ss')) {
      return label.slice(0, -1);
    }
    
    // Return as-is if no plural pattern found
    return label;
  }
}
